// =============================================================================
// BP-07-03: ResourceMeta Component
// Article metadata display (author, date, reading time, etc.)
// =============================================================================

import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  User,
  Eye,
  Bookmark,
  Tag,
  FolderOpen,
} from 'lucide-react';
import type { Resource, ResourceType, LifeStage } from '@/features/resources/types/resourcesTypes';
import { resourceTypeLabels, lifeStageLabels } from '@/features/resources/types/resourcesTypes';
import { RatingDisplay } from './RatingWidget';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface ResourceMetaProps {
  resource: Resource;
  variant?: 'default' | 'compact' | 'detailed';
  showRating?: boolean;
  showViews?: boolean;
  showSaves?: boolean;
  showReadingTime?: boolean;
  showTags?: boolean;
  showCategory?: boolean;
  showLifeStage?: boolean;
  className?: string;
}

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  
  return formatDate(dateString);
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ResourceMeta({
  resource,
  variant = 'default',
  showRating = true,
  showViews = true,
  showSaves = false,
  showReadingTime = true,
  showTags = true,
  showCategory = true,
  showLifeStage = false,
  className,
}: ResourceMetaProps) {
  // Compact variant
  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center gap-3 text-sm text-warm-gray', className)}>
        {resource.publishedAt && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {formatRelativeDate(resource.publishedAt)}
          </span>
        )}
        {showReadingTime && resource.readingTimeMinutes && (
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {resource.readingTimeMinutes} min read
          </span>
        )}
        {showRating && resource.avgRating && resource.avgRating > 0 && (
          <RatingDisplay rating={resource.avgRating} size="sm" showValue />
        )}
      </div>
    );
  }

  // Detailed variant
  if (variant === 'detailed') {
    return (
      <div className={cn('space-y-4', className)}>
        {/* Author and date row */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {resource.authorName && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center">
                <User className="w-4 h-4 text-sage" />
              </div>
              <div>
                <div className="font-medium text-navy">{resource.authorName}</div>
                <div className="text-xs text-warm-gray">Author</div>
              </div>
            </div>
          )}
          
          {resource.publishedAt && (
            <div className="flex items-center gap-2 text-warm-gray">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(resource.publishedAt)}</span>
            </div>
          )}
          
          {showReadingTime && resource.readingTimeMinutes && (
            <div className="flex items-center gap-2 text-warm-gray">
              <Clock className="w-4 h-4" />
              <span>{resource.readingTimeMinutes} min read</span>
            </div>
          )}
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {showRating && (
            <div className="flex items-center gap-2">
              <RatingDisplay
                rating={resource.avgRating || 0}
                size="md"
                showValue
              />
              {resource.ratingCount && resource.ratingCount > 0 && (
                <span className="text-warm-gray">
                  ({resource.ratingCount} ratings)
                </span>
              )}
            </div>
          )}
          
          {showViews && resource.viewCount !== undefined && (
            <div className="flex items-center gap-1 text-warm-gray">
              <Eye className="w-4 h-4" />
              <span>{resource.viewCount.toLocaleString()} views</span>
            </div>
          )}
          
          {showSaves && resource.saveCount !== undefined && (
            <div className="flex items-center gap-1 text-warm-gray">
              <Bookmark className="w-4 h-4" />
              <span>{resource.saveCount.toLocaleString()} saves</span>
            </div>
          )}
        </div>

        {/* Category and type */}
        {(showCategory || showLifeStage) && (
          <div className="flex flex-wrap items-center gap-2">
            {showCategory && resource.category && (
              <Link
                to={`/resources/category/${resource.category.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-cream hover:bg-cream-dark text-navy text-sm font-medium rounded-full transition-colors"
              >
                <FolderOpen className="w-3.5 h-3.5" />
                {resource.category.name}
              </Link>
            )}
            
            {resource.resourceType && (
              <span className="inline-flex items-center px-3 py-1 bg-sage/10 text-sage text-sm font-medium rounded-full">
                {resourceTypeLabels[resource.resourceType]}
              </span>
            )}
            
            {showLifeStage && resource.lifeStage && (
              <span className="inline-flex items-center px-3 py-1 bg-terracotta/10 text-terracotta text-sm font-medium rounded-full">
                {lifeStageLabels[resource.lifeStage]}
              </span>
            )}
          </div>
        )}

        {/* Tags */}
        {showTags && resource.tags && resource.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="w-4 h-4 text-warm-gray" />
            {resource.tags.map((tag) => (
              <Link
                key={tag}
                to={`/resources/search?tag=${encodeURIComponent(tag)}`}
                className="text-sm text-warm-gray hover:text-sage transition-colors"
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-2 text-sm', className)}>
      {/* Author */}
      {resource.authorName && (
        <div className="flex items-center gap-1.5 text-warm-gray">
          <User className="w-4 h-4" />
          <span>{resource.authorName}</span>
        </div>
      )}

      {/* Published date */}
      {resource.publishedAt && (
        <div className="flex items-center gap-1.5 text-warm-gray">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(resource.publishedAt)}</span>
        </div>
      )}

      {/* Reading time */}
      {showReadingTime && resource.readingTimeMinutes && (
        <div className="flex items-center gap-1.5 text-warm-gray">
          <Clock className="w-4 h-4" />
          <span>{resource.readingTimeMinutes} min read</span>
        </div>
      )}

      {/* Views */}
      {showViews && resource.viewCount !== undefined && resource.viewCount > 0 && (
        <div className="flex items-center gap-1.5 text-warm-gray">
          <Eye className="w-4 h-4" />
          <span>{resource.viewCount.toLocaleString()}</span>
        </div>
      )}

      {/* Rating */}
      {showRating && resource.avgRating && resource.avgRating > 0 && (
        <RatingDisplay rating={resource.avgRating} size="sm" showValue />
      )}

      {/* Category */}
      {showCategory && resource.category && (
        <Link
          to={`/resources/category/${resource.category.slug}`}
          className="flex items-center gap-1.5 text-sage hover:text-sage-dark transition-colors"
        >
          <FolderOpen className="w-4 h-4" />
          <span>{resource.category.name}</span>
        </Link>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// ResourceTypeTag
// -----------------------------------------------------------------------------

interface ResourceTypeTagProps {
  type: ResourceType;
  size?: 'sm' | 'md';
  className?: string;
}

export function ResourceTypeTag({ type, size = 'sm', className }: ResourceTypeTagProps) {
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full bg-sage/10 text-sage',
        sizeClasses[size],
        className
      )}
    >
      {resourceTypeLabels[type]}
    </span>
  );
}

// -----------------------------------------------------------------------------
// LifeStageTag
// -----------------------------------------------------------------------------

interface LifeStageTagProps {
  stage: LifeStage;
  size?: 'sm' | 'md';
  className?: string;
}

export function LifeStageTag({ stage, size = 'sm', className }: LifeStageTagProps) {
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full bg-terracotta/10 text-terracotta',
        sizeClasses[size],
        className
      )}
    >
      {lifeStageLabels[stage]}
    </span>
  );
}

export default ResourceMeta;

